import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Panel } from "@/components/common";
import { api, errMsg, fmtDate, todayIso } from "@/lib/api";
import { cn } from "@/lib/utils";

function ClosureDialog({ onClose, onDone }) {
  const t = todayIso();
  const [f, setF] = useState({ title: "", start_date: t, end_date: t, kind: "closure", notify_employees: true });
  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/closures", f);
      toast.success(f.kind === "closure" ? "Chiusura aggiunta" : "Periodo bloccato aggiunto");
      onClose();
      onDone();
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="closure-dialog">
        <DialogHeader>
          <DialogTitle>Nuova chiusura o periodo bloccato</DialogTitle>
          <DialogDescription>Le chiusure non vengono conteggiate come ferie. Nei periodi bloccati i dipendenti non possono richiedere ferie.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <RadioGroup value={f.kind} onValueChange={(v) => setF({ ...f, kind: v })} className="grid grid-cols-2 gap-2">
            {[["closure", "Chiusura aziendale", "Negozio/azienda chiusa"], ["blackout", "Periodo bloccato", "Alta intensità di lavoro"]].map(([v, l, d]) => (
              <label key={v} className="flex cursor-pointer items-start gap-2.5 rounded-md border p-3 text-sm">
                <RadioGroupItem value={v} className="mt-0.5" data-testid={`closure-kind-${v}`} />
                <span><span className="block font-medium">{l}</span><span className="text-xs text-muted-foreground">{d}</span></span>
              </label>
            ))}
          </RadioGroup>
          <div className="space-y-1.5"><Label>Titolo</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required minLength={2} placeholder="es. Chiusura estiva" data-testid="closure-title-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Dal</Label><Input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} required data-testid="closure-start-input" /></div>
            <div className="space-y-1.5"><Label>Al</Label><Input type="date" min={f.start_date} value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} required data-testid="closure-end-input" /></div>
          </div>
          <label className="flex items-center justify-between rounded-md border px-3 py-2.5 text-sm">Notifica i dipendenti<Switch checked={f.notify_employees} onCheckedChange={(c) => setF({ ...f, notify_employees: c })} data-testid="closure-notify-switch" /></label>
          <DialogFooter><Button type="submit" data-testid="closure-submit-button">Salva</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function ClosuresPanel({ onChange }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/admin/closures").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const refresh = () => { load(); onChange(); };
  const remove = async (id) => { await api.delete(`/admin/closures/${id}`); toast.success("Eliminato"); refresh(); };
  return (
    <Panel title="Chiusure e periodi bloccati" className="mt-6" testId="closures-panel"
      action={<Button size="sm" onClick={() => setOpen(true)} data-testid="new-closure-button"><Plus className="mr-1 h-4 w-4" />Aggiungi</Button>}>
      {items.length === 0 ? <p className="px-5 py-6 text-sm text-muted-foreground">Nessuna chiusura o periodo bloccato.</p> : (
        <ul className="divide-y">
          {items.map((c) => (
            <li key={c.id} className="flex items-center gap-3 px-5 py-3 text-sm" data-testid={`closure-row-${c.id}`}>
              <span className={cn("h-2.5 w-2.5 rounded-full", c.kind === "closure" ? "bg-red-500" : "bg-amber-400")} />
              <span className="flex-1"><span className="font-medium">{c.title}</span><span className="ml-2 text-xs text-muted-foreground">{c.kind === "closure" ? "Chiusura" : "Ferie bloccate"}</span></span>
              <span className="text-xs text-muted-foreground">{fmtDate(c.start_date)} → {fmtDate(c.end_date)}</span>
              <Button size="icon" variant="ghost" onClick={() => remove(c.id)} data-testid={`closure-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
            </li>
          ))}
        </ul>
      )}
      {open && <ClosureDialog onClose={() => setOpen(false)} onDone={refresh} />}
    </Panel>
  );
}
